import React, { useState, useRef } from "react";
import { enviarMensagem } from "../services/services";
import ReCAPTCHA from "react-google-recaptcha";
import { FaSpinner, FaCheckCircle } from "react-icons/fa";

const SecaoContato = () => {
  const [formData, setFormData] = useState({
    nome: "",
    cpf: "",
    telefone: "",
    email: "",
    assunto: "",
    mensagem: ""
  });

  const [errors, setErrors] = useState({
    nome: "",
    cpf: "",
    email: "",
    mensagem: "",
    geral: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef();

  // Função para aplicar máscara de CPF
  const maskCPF = (value) => {
    // Remove tudo que não é dígito
    let cpf = value.replace(/\D/g, "");
    
    // Limita a 11 caracteres
    cpf = cpf.substring(0, 11);
    
    // Aplica a máscara: 000.000.000-00
    cpf = cpf.replace(/(\d{3})(\d)/, "$1.$2");
    cpf = cpf.replace(/(\d{3})(\d)/, "$1.$2");
    cpf = cpf.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    
    return cpf;
  };

    const validateCPF = (cpf) => {
    // Remove caracteres não numéricos
    cpf = cpf.replace(/\D/g, '');
    
    // Verifica se tem 11 dígitos e não é uma sequência de números iguais
    if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) {
      return false;
    }

    // Validação do primeiro dígito verificador
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cpf.charAt(i)) * (10 - i);
    }
    let remainder = 11 - (sum % 11);
    let digit = remainder >= 10 ? 0 : remainder;
    if (digit !== parseInt(cpf.charAt(9))) {
      return false;
    }

    // Validação do segundo dígito verificador
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cpf.charAt(i)) * (11 - i);
    }
    remainder = 11 - (sum % 11);
    digit = remainder >= 10 ? 0 : remainder;
    if (digit !== parseInt(cpf.charAt(10))) {
      return false;
    }

    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Aplica máscara apenas para o campo CPF
    if (name === "cpf") {
      const maskedValue = maskCPF(value);
      setFormData({ ...formData, [name]: maskedValue });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    
    // Limpa o erro do campo quando o usuário começa a digitar
    if (errors[name]) {
      setErrors({ ...errors, [name]: "", geral: "" });
    }
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    if (errors.geral === "Por favor, valide que você não é um robô.") {
      setErrors({ ...errors, geral: "" });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    if (!formData.nome.trim()) {
      newErrors.nome = "Nome é obrigatório";
      isValid = false;
    }

    // Remove a máscara para validação
     const cpfNumerico = formData.cpf.replace(/\D/g, "");
    if (!cpfNumerico) {
      newErrors.cpf = "CPF é obrigatório";
      isValid = false;
    } else if (cpfNumerico.length !== 11) {
      newErrors.cpf = "CPF deve ter 11 dígitos";
      isValid = false;
    } else if (!validateCPF(formData.cpf)) {
      newErrors.cpf = "CPF inválido";
      isValid = false;
    }

    if (!formData.email.trim()) {
      newErrors.email = "E-mail é obrigatório";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "E-mail inválido";
      isValid = false;
    }

    if (!formData.mensagem.trim()) {
      newErrors.mensagem = "Mensagem é obrigatória";
      isValid = false;
    }

    if (!recaptchaToken) {
      newErrors.geral = "Por favor, valide que você não é um robô.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const mensagemData = {
        canal: "APP",
        assunto: formData.assunto || "Contato via site",
        conteudo: formData.mensagem,
        situacaoEnvio: "PENDENTE",
        remetente: {
          nome: formData.nome,
          email: formData.email,
          telefone: formData.telefone,
          // Envia apenas os números do CPF
          numeroDocumento: formData.cpf.replace(/\D/g, "")
        }
      };

      await enviarMensagem(mensagemData);

      setIsSuccess(true);
      setFormData({
        nome: "",
        cpf: "",
        telefone: "",
        email: "",
        assunto: "",
        mensagem: ""
      });
      
      // Reset reCAPTCHA
      recaptchaRef.current.reset();
      setRecaptchaToken(null);
      
      setTimeout(() => setIsSuccess(false), 5000);
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
      setErrors({ ...errors, geral: "Ocorreu um erro ao enviar sua mensagem. Por favor, tente novamente mais tarde." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 px-4 bg-gray-50">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-8">
        <div className="text-center mb-10">
          <h2 className="text-4xl font-bold text-gray-800">
            <span className="inline-block pb-2 border-b-4 border-yellow-400 px-6">
              Contato
            </span>
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Fale com a Comissão Geral de Eleição
          </p>
        </div>

        {isSuccess ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
              <FaCheckCircle className="text-green-500 text-4xl" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-2">Mensagem enviada com sucesso!</h3>
            <p className="text-gray-600 mb-6">Agradecemos pelo seu contato. Nossa equipe entrará em contato em breve.</p>
            <button
              onClick={() => setIsSuccess(false)}
              className="px-6 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 transition-colors"
            >
              Enviar nova mensagem
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  className={`mt-1 w-full border ${errors.nome ? 'border-red-500' : 'border-gray-300'} px-4 py-3 rounded-md focus:ring-2 focus:ring-yellow-400 focus:border-transparent`}
                  placeholder="Digite seu nome completo"
                  disabled={isSubmitting}
                />
                {errors.nome && <p className="mt-1 text-sm text-red-600">{errors.nome}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CPF <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="cpf"
                  value={formData.cpf}
                  onChange={handleChange}
                  className={`mt-1 w-full border ${errors.cpf ? 'border-red-500' : 'border-gray-300'} px-4 py-3 rounded-md focus:ring-2 focus:ring-yellow-400 focus:border-transparent`}
                  placeholder="Digite seu CPF"
                  disabled={isSubmitting}
                  maxLength={14} // 11 dígitos + 3 caracteres da máscara
                />
                {errors.cpf && <p className="mt-1 text-sm text-red-600">{errors.cpf}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefone
                </label>
                <input
                  type="tel"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleChange}
                  className="mt-1 w-full border border-gray-300 px-4 py-3 rounded-md focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  placeholder="Digite seu telefone"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-mail <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`mt-1 w-full border ${errors.email ? 'border-red-500' : 'border-gray-300'} px-4 py-3 rounded-md focus:ring-2 focus:ring-yellow-400 focus:border-transparent`}
                  placeholder="Digite seu e-mail"
                  disabled={isSubmitting}
                />
                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assunto
                </label>
                <input
                  type="text"
                  name="assunto"
                  value={formData.assunto}
                  onChange={handleChange}
                  className="mt-1 w-full border border-gray-300 px-4 py-3 rounded-md focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  placeholder="Digite qual o assunto"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mensagem <span className="text-red-500">*</span>
              </label>
              <textarea
                name="mensagem"
                value={formData.mensagem}
                onChange={handleChange}
                className={`mt-1 w-full border ${errors.mensagem ? 'border-red-500' : 'border-gray-300'} px-4 py-3 rounded-md focus:ring-2 focus:ring-yellow-400 focus:border-transparent min-h-[150px]`}
                placeholder="Escreva sua mensagem aqui..."
                disabled={isSubmitting}
              />
              {errors.mensagem && <p className="mt-1 text-sm text-red-600">{errors.mensagem}</p>}
            </div>

            <div className="flex justify-center">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={handleRecaptchaChange}
              />
            </div>

            {errors.geral && (
              <div className="p-4 bg-red-50 rounded-md">
                <p className="text-red-600 text-center">{errors.geral}</p>
              </div>
            )}

            <div className="pt-4 mx-auto flex justify-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full md:w-auto px-8 py-3 rounded-md font-medium mx-auto text-white bg-gray-800 hover:bg-gray-700 transition-colors ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <FaSpinner className="animate-spin mr-2" />
                    Enviando...
                  </span>
                ) : (
                  "Enviar Mensagem"
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};

export default SecaoContato;